import {
    CANNOT_DELETE_ITEM_IN_USE_MSG,
    CloseButton,
    CrudMode,
    DELETE_WARNING,
    DropDownMenu,
    ListContext,
    ListContextValue,
    MenuItemType,
    Stack,
    Switch,
    Typography,
    UpdateButton,
    ValidationErrors,
    newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, Permission, PermissionAction, DiplomaType } from "generated/openApi";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const DiplomaTypePageHeader = (props: {
    rootPermission: Permission;
    handle: string;
    crudMode: CrudMode;
    fetch: (id: number) => void;
    reroute: (id: number, crudMode: CrudMode) => void;
    diplomaType: DiplomaType;
    setDiplomaType: (diplomaType: DiplomaType) => void;
    initDiplomaType: DiplomaType;
    setValidationErrors: (errors: ValidationErrors) => void;
    unsavedChanges: boolean;
    resetUnsavedChanges: (items: any[]) => void;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const listContext = useContext<ListContextValue>(ListContext);
    const id = props.diplomaType.id;
    const [createAnother, setCreateAnother] = useState<boolean>(false);

    const save = (diplomaType: DiplomaType) => {
        props.setValidationErrors({});

        if (id > 0) {
            ApiService.diplomaTypes
                .saveUpdate(diplomaType)
                .then(() => {
                    props.fetch(id);
                    listContext.loadList?.();
                    dispatch(setAppToast(newToast("success")));
                    props.reroute(id, "view");
                })
                .catch((ex) => {
                    dispatch(setAppToast(newToast("error")));
                    props.setValidationErrors(ex.response);
                });
        } else {
            ApiService.diplomaTypes
                .saveCreate(diplomaType)
                .then((response) => {
                    const id = response;
                    if (createAnother) {
                        props.setDiplomaType(props.initDiplomaType);
                        props.resetUnsavedChanges([props.initDiplomaType]);
                    } else {
                        props.fetch(id);
                        props.reroute(id, "view");
                    }
                    listContext.loadList?.();
                    dispatch(setAppToast(newToast("success")));
                })
                .catch((ex) => {
                    dispatch(setAppToast(newToast("error")));
                    props.setValidationErrors(ex.response);
                });
        }
    };

    const postDelete = (id: number) => {
        if (window.confirm(DELETE_WARNING)) {
            ApiService.diplomaTypes.delete(id).then(() => {
                navigate("../");
                listContext.loadList?.();
                dispatch(setAppToast(newToast("success")));
            });
        }
    };

    const menuItems: MenuItemType[] = [];
    if (id > 0) {
        if (isAuthorized(props.rootPermission, PermissionAction.Delete))
            menuItems.push({
                label: <Typography color="error">Delete</Typography>,
                action: () => postDelete(id),
                disabled: props.diplomaType.isInUse,
                tooltip: props.diplomaType.isInUse ? CANNOT_DELETE_ITEM_IN_USE_MSG : undefined,
            });
    }

    return (
        <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing="sm">
                <Typography variant="inherit">
                    {id === 0 ? "New" : ""} {props.handle}
                </Typography>
                {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
            </Stack>

            <Stack direction="row" alignItems="center" spacing="sm">
                {id > 0 && <DataModelInformation dataModelType={DataModelEnum.DiplomaType} dataModelId={id} />}

                {id === 0 && (
                    <Switch value={createAnother} onChange={(e) => setCreateAnother(e.target.checked)} label="+ more" />
                )}

                <CloseButton theme="Dark" confirm={props.unsavedChanges} />

                {props.crudMode === "view" && isAuthorized(props.rootPermission, PermissionAction.Update) && (
                    <UpdateButton theme="Dark" onClick={() => props.reroute(id, "edit")} />
                )}

                {props.crudMode === "edit" && (
                    <SubmitButton theme="Dark" onClick={() => save(props.diplomaType)} disabled={!props.unsavedChanges} />
                )}
            </Stack>
        </Stack>
    );
};
